/**
 * The insight a page has open, for OS (web-redesign-10 phase 5, Decided 5).
 *
 * An insight's own page, and the page that changes one by asking, say which
 * insight is in front of the person: its card as the chat context carries
 * cards (`AgentScreenCard`), marked open, and on its own page what it shows
 * now. Every turn and every live place reads it (`withOpenInsight`), so "this
 * insight" means it and a question about it is answered from its numbers.
 *
 * One at a time, the page showing; it lets go when the page goes. The
 * conversation OS keeps beside each insight is held here too, by the
 * insight's key, so coming back to it finds the same talk.
 */
import { AGENT_SCREEN_CARD_NUMBERS_MAX, formatWeekdayDay, type AgentScreenCard } from '@alluzo/shared';
import { useEffect } from 'react';
import { create } from 'zustand';

import type { Presented } from '@/lib/insight-present';

type OpenInsightState = {
  /** The card open now, and the page's key for it. */
  readonly open: { readonly key: string; readonly card: AgentScreenCard } | null;
  /** The conversation beside each insight, by the insight's key. */
  readonly threads: Readonly<Record<string, string>>;
  readonly report: (key: string, card: AgentScreenCard) => void;
  readonly clear: (key: string) => void;
  readonly holdThread: (key: string, threadId: string) => void;
};

export const useOpenInsightStore = create<OpenInsightState>()((set, get) => ({
  open: null,
  threads: {},
  report: (key, card) => {
    const held = get().open;
    if (held !== null && held.key === key && JSON.stringify(held.card) === JSON.stringify(card)) return;
    set({ open: { key, card } });
  },
  clear: (key) => {
    if (get().open?.key === key) set({ open: null });
  },
  holdThread: (key, threadId) => set((state) => (state.threads[key] === threadId ? state : { threads: { ...state.threads, [key]: threadId } })),
}));

/** The page says which insight it has open, for as long as it shows it. Null lets go. */
export const useReportOpenInsight = (key: string, card: AgentScreenCard | null): void => {
  const said = card === null ? null : JSON.stringify(card);
  useEffect(() => {
    if (said === null) return undefined;
    useOpenInsightStore.getState().report(key, JSON.parse(said) as AgentScreenCard);
    return () => useOpenInsightStore.getState().clear(key);
  }, [key, said]);
};

/**
 * Calls `onChange` whenever the open insight changes: opened, let go, or its
 * numbers drawn. A live call's place reads it, and an insight's page reports
 * its card only once its numbers are in, which may be after the place first
 * went up. Returns the way to stop.
 */
export const watchOpenInsight = (onChange: () => void): (() => void) =>
  useOpenInsightStore.subscribe((next, before) => {
    if (next.open !== before.open) onChange();
  });

/** The open insight's name, for the chip and the head of OS beside it; null with none open. */
export const useOpenInsightLabel = (): string | null => useOpenInsightStore((state) => state.open?.card.label ?? null);

/**
 * The cards a turn carries, with the open one marked: the same card in the
 * list takes its open self, and one the list does not hold (a record's insight
 * asked from the Dashboard's place) is added.
 */
export const cardsWithOpen = (cards: readonly AgentScreenCard[], open: AgentScreenCard | null): AgentScreenCard[] => {
  if (open === null) return [...cards];
  const at = cards.findIndex((card) => card.id === open.id);
  if (at === -1) return [...cards, open];
  return cards.map((card, index) => (index === at ? open : card));
};

/** `cardsWithOpen` with the insight open now. */
export const withOpenInsight = (cards: readonly AgentScreenCard[]): AgentScreenCard[] =>
  cardsWithOpen(cards, useOpenInsightStore.getState().open?.card ?? null);

/** The most days and groups an open card names one by one. */
const DAYS_MAX = 31;
const GROUPS_MAX = 12;

/**
 * What an insight shows, in a few words for OS (not for a person to read):
 * its headline, its change, its groups or its days, each figure as the page
 * formats it. The prompt hands it to the model as data; the scripted model
 * reads "By day:" back. Cut to the length the server keeps.
 */
export const insightNumbersOf = (presented: Presented, format: (value: number) => string): string => {
  const parts: string[] = [];
  if (presented.empty) parts.push('Nothing counted yet.');
  else if (presented.value !== '') parts.push(`${presented.value}.`);
  if (presented.change !== null) parts.push(`${presented.change.percent} ${presented.change.against}.`);
  if (presented.slices.length > 0) {
    const groups = presented.slices.slice(0, GROUPS_MAX).map((slice) => `${slice.label} ${slice.value}`);
    if (presented.other !== null) groups.push(`${presented.other.label} ${presented.other.value}`);
    parts.push(`By group: ${groups.join('; ')}.`);
  }
  if (presented.points.length > 0) {
    const days = presented.points.slice(-DAYS_MAX).map((point) => `${formatWeekdayDay(point.day)} ${format(point.value).replace(/\s+/g, '')}`);
    parts.push(`By day: ${days.join('; ')}.`);
  }
  if (presented.sample) parts.push('These are sample numbers; nothing is recorded yet.');
  const said = parts.join(' ');
  return said.length <= AGENT_SCREEN_CARD_NUMBERS_MAX ? said : `${said.slice(0, AGENT_SCREEN_CARD_NUMBERS_MAX - 1)}…`;
};
